import JobSearchAdapter from 'jobs/infra/JobSearchAdapter'
import { makeObservable, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { Subscription, filter } from 'rxjs'
import Workspace from 'iam/models/Workspace'
import JobSearchQueryStringBuilder from 'jobs/infra/JobSearchQueryStringBuilder'
import { JobScopeOption } from 'jobs/dtos/job'
import { JobStatusFilterOption } from 'jobs/views/JobManagement/JobStatusFilterOption'
import { DateRange } from 'infra/FilterControls/DateRange/DateRangeFilter'
import FirmDashboardContractViewModel from '../FirmDashboardContractViewModel'

/**
 * View model for managing the quick select filters.
 */
export default class FirmDashboardQuickSelectFilterViewModel {
  contractInfo = new FirmDashboardContractViewModel()

  /**
   * Current workspace to filter by.
   */
  @observable
  workspace = Workspace.none

  /**
   * Total count for under construction jobs.
   */
  @observable
  underConstructionJobsCount: number = 0

  /**
   * Total count for in progress jobs.
   */
  @observable
  inProgressJobsCount: number = 0

  /**
   * Total count for completed jobs.
   */
  @observable
  completedJobsCount: number = 0

  /**
   * Observable subscription used for disposing on deactivate.
   */
  private subscription = new Subscription()

  /**
   * Constructor.
   */
  constructor(private jobFetcher: JobSearchAdapter) {
    makeObservable(this)
  }

  getTotalJobsFilterCount(option: JobStatusFilterOption) {
    switch (option) {
      case JobStatusFilterOption.Onboarding:
        return this.underConstructionJobsCount
      case JobStatusFilterOption.InProgress:
        return this.inProgressJobsCount
      case JobStatusFilterOption.Closed:
        return this.completedJobsCount
    }
  }

  mapJobStatusOptionToString(option: JobStatusFilterOption) {
    switch (option) {
      case JobStatusFilterOption.Onboarding:
        return 'underConstruction'
      case JobStatusFilterOption.InProgress:
        return 'inProgress'
      case JobStatusFilterOption.Closed:
        return 'completed'
    }
  }

  /**
   * Sets the workspace filter.
   * @param workspace Workspace to filter by.
   */
  async setWorkspace(workspace: Workspace) {
    this.workspace = workspace
    this.contractInfo.setCurrentWorkspace(workspace)
    await this.fetchJobsStatusCount(workspace)
  }

  /**
   * Fetch the total count for jobs with status:
   * `UNDER_CONSTRUCTION`, `CLAIMED` and `CLOSED`
   * @param workspace
   */
  private async fetchJobsStatusCount(workspace: Workspace) {
    const [underConstructionCount, inProgressCount, completedCount] =
      await Promise.all([
        this.jobFetcher.getTotal({
          scope: JobScopeOption.All,
          filter: new JobSearchQueryStringBuilder()
            .withWorkspaceIds([workspace.id])
            .withStatuses(['UNDER_CONSTRUCTION'])
            .withinDateCreatedRange({
              from: this.contractInfo.contractStartDate,
              to: this.contractInfo.contractEndDate,
            } as DateRange)
            .build(),
          $limit: 0,
        }),
        this.jobFetcher.getTotal({
          scope: JobScopeOption.All,
          filter: new JobSearchQueryStringBuilder()
            .withWorkspaceIds([workspace.id])
            .withStatuses(['CLAIMED', 'ON_HOLD', 'IDLE'])
            .withinDateCreatedRange({
              from: this.contractInfo.contractStartDate,
              to: this.contractInfo.contractEndDate,
            } as DateRange)
            .build(),
          $limit: 0,
        }),
        this.jobFetcher.getTotal({
          scope: JobScopeOption.All,
          filter: new JobSearchQueryStringBuilder()
            .withWorkspaceIds([workspace.id])
            .withStatuses(['CLOSED'])
            .withinDateCreatedRange({
              from: this.contractInfo.contractStartDate,
              to: this.contractInfo.contractEndDate,
            } as DateRange)
            .build(),
          $limit: 0,
        }),
      ])

    runInAction(() => {
      this.underConstructionJobsCount = underConstructionCount
      this.inProgressJobsCount = inProgressCount
      this.completedJobsCount = completedCount
    })
  }

  /**
   * Activates the view model.
   */
  activate = task.resolved(async () => {
    this.subscription = new Subscription()
    this.subscription = this.jobFetcher.events$
      .pipe(filter((event) => event.type === 'JobUpdated'))
      .subscribe(async (event) => {
        if (event.job.workspace.id === this.workspace.id) {
          await this.fetchJobsStatusCount(this.workspace)
        }
      })
  })

  /**
   * Called when the view model is not being actively used.
   */
  deactivate() {
    this.subscription.unsubscribe()
  }
}
